class GiftProgressComponent extends HTMLElement {
  constructor() {
    super();
    this.progressBar = this.querySelector('[data-gift-progress-meter]');
    this.progressText = this.progressBar?.querySelector('.text');
    this.messageEl = this.querySelector('[data-gift-message]');
    this.progressContainer = this.querySelector('[data-gift-progress]');
  }

  connectedCallback() {
    // Armazenar referência do listener para poder remover depois
    this.boundUpdateProgress = this.updateProgress.bind(this);

    // Escutar eventos de atualização do carrinho
    document.addEventListener('cart-update', this.boundUpdateProgress);

    // Aplicar cores personalizadas
    this.applyCustomColors();

    // Atualizar na inicialização com delay para garantir que config está carregado
    setTimeout(() => this.updateProgress(), 500);
  }

  /**
   * Aplica cores personalizadas via CSS variables
   */
  applyCustomColors() {
    const config = window.giftSystemConfig;
    if (!config || !config.colors) {
      return;
    }

    const colors = config.colors;

    // Aplicar CSS variables no elemento raiz (haloCalculatorGift)
    if (this.style) {
      this.style.setProperty('--gift-progress-color-30', colors.progress30 || '#a78bfa');
      this.style.setProperty('--gift-progress-color-60', colors.progress60 || '#8b5cf6');
      this.style.setProperty('--gift-progress-color-100', colors.progress100 || '#7c3aed');
      this.style.setProperty('--gift-progress-color-unlocked', colors.progressUnlocked || '#7c3aed');
      this.style.setProperty('--gift-progress-bg-color', colors.progressBg || '#f0f0f0');
      this.style.setProperty('--gift-progress-text-color', colors.progressText || '#ffffff');
      this.style.setProperty('--gift-message-color-default', colors.messageDefault || '#666666');
      this.style.setProperty('--gift-message-color-30', colors.message30 || '#a78bfa');
      this.style.setProperty('--gift-message-color-60', colors.message60 || '#8b5cf6');
      this.style.setProperty('--gift-message-color-100', colors.message100 || '#7c3aed');
      this.style.setProperty('--gift-message-color-unlocked', colors.messageUnlocked || '#7c3aed');
    }

    // Também aplicar no progress container se existir
    if (this.progressContainer && this.progressContainer.style) {
      this.progressContainer.style.setProperty('--gift-progress-color-30', colors.progress30 || '#a78bfa');
      this.progressContainer.style.setProperty('--gift-progress-color-60', colors.progress60 || '#8b5cf6');
      this.progressContainer.style.setProperty('--gift-progress-color-100', colors.progress100 || '#7c3aed');
      this.progressContainer.style.setProperty('--gift-progress-color-unlocked', colors.progressUnlocked || '#7c3aed');
      this.progressContainer.style.setProperty('--gift-progress-bg-color', colors.progressBg || '#f0f0f0');
      this.progressContainer.style.setProperty('--gift-progress-text-color', colors.progressText || '#ffffff');
    }
  }

  async updateProgress() {
    try {
      // Reaplicar cores personalizadas (caso tenham mudado)
      this.applyCustomColors();

      // Usar configuração global do gift system
      const config = window.giftSystemConfig;

      if (!config || !config.enabled || !config.levels || config.levels.length === 0) {
        this.style.display = 'none';
        return;
      }

      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Filtrar apenas níveis ativos
      const activeLevels = config.levels.filter(level => level.enabled && level.availableGifts && level.availableGifts.length > 0);

      if (activeLevels.length === 0) {
        this.style.display = 'none';
        return;
      }

      // Calcular total do carrinho (em centavos)
      const cartTotal = cart.total_price;

      // Converter minValue para centavos (minValue está em reais)
      const sortedLevels = [...activeLevels].sort((a, b) => a.minValue - b.minValue);
      const nextLevel = sortedLevels.find(level => cartTotal < (level.minValue * 100));
      const currentLevel = sortedLevels.filter(level => cartTotal >= (level.minValue * 100)).pop();

      if (!nextLevel && !currentLevel) {
        this.style.display = 'none';
        return;
      }

      this.style.display = 'block';

      if (currentLevel && !nextLevel) {
        // Verificar se realmente desbloqueou (valor + trigger products)
        const isFullyUnlocked = this.checkLevelEligibility(currentLevel, cart);
        const giftHandle = currentLevel.availableGifts[0];

        if (isFullyUnlocked) {
          // ✅ Valor atingido E trigger product presente (se configurado)
          this.updateUI(100, `🎁 Parabéns! Você ganhou um brinde!`, 'progress-free');
        } else {
          // ⚠️ Valor atingido MAS falta trigger product
          const message = `Adicione um dos produtos especiais para desbloquear o brinde!`;
          this.updateUI(100, message, 'progress-100');
        }
      } else if (nextLevel) {
        // Calcular progresso
        const thresholdCents = nextLevel.minValue * 100;
        const remainingCents = thresholdCents - cartTotal;
        const progress = Math.min(100, Math.floor((cartTotal / thresholdCents) * 100));

        const giftHandle = nextLevel.availableGifts[0];
        const remainingFormatted = (remainingCents / 100).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        });

        let progressClass = 'progress-30';
        if (progress >= 60) progressClass = 'progress-60';
        if (progress >= 100) progressClass = 'progress-100';

        const message = `Faltam ${remainingFormatted} para ganhar um brinde!`;
        this.updateUI(progress, message, progressClass);
      } else {
        this.style.display = 'none';
      }
    } catch (error) {
      // Erro silencioso
    }
  }

  updateUI(progress, message, progressClass) {
    if (this.progressBar) {
      this.progressBar.style.width = `${progress}%`;
    }

    if (this.progressText) {
      this.progressText.textContent = `${progress}%`;
    }

    if (this.messageEl) {
      this.messageEl.textContent = message;
      this.messageEl.className = `message ${progressClass}`;
    }

    if (this.progressContainer) {
      this.progressContainer.className = `progress ${progressClass}`;
    }
  }

  /**
   * Verifica se um nível está completamente desbloqueado
   * (valor mínimo + trigger products se configurado)
   */
  checkLevelEligibility(level, cart) {
    // Se não tem trigger products configurado, só valor já basta
    if (!level.triggerProducts || level.triggerProducts.length === 0) {
      return true;
    }

    // Verificar se algum dos trigger products está no carrinho
    const hasTriggerProduct = cart.items.some(item => {
      // Ignorar itens que são brindes
      if (item.properties && item.properties._is_gift === 'true') {
        return false;
      }

      return level.triggerProducts.some(triggerProduct => {
        // triggerProduct pode ser string (handle/id) ou objeto { handle: "..." }
        const productIdentifier = typeof triggerProduct === 'object' && triggerProduct !== null
          ? triggerProduct.handle
          : triggerProduct;

        // Normalizar: remover GID se presente
        const cleanIdentifier = productIdentifier && productIdentifier.toString().includes('gid://shopify/Product/')
          ? productIdentifier.split('/').pop()
          : productIdentifier;

        // Comparar por ID ou handle
        const matchById = item.product_id == cleanIdentifier ||
                          item.product_id === parseInt(cleanIdentifier);
        const matchByHandle = item.handle &&
                              (item.handle === cleanIdentifier ||
                               item.handle === productIdentifier);

        return matchById || matchByHandle;
      });
    });

    return hasTriggerProduct;
  }

  /**
   * Limpa event listeners quando o elemento é removido do DOM
   */
  disconnectedCallback() {
    if (this.boundUpdateProgress) {
      document.removeEventListener('cart-update', this.boundUpdateProgress);
    }
  }
}

customElements.define('gift-progress-component', GiftProgressComponent);
